import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  message,
  Card,
  Typography,
  Table,
  Modal,
  Space,
  Row,
  Col,
} from "antd";
import "../PhieuDatTT/BanhBan.css"; // Import the CSS file
import moment from "moment";
import NavbarTrungThu from "../Layout/NavbarTrungThu"
import {
  createKpiSeason,
  fetchKpiBranches,
  fetchKpiSeasons,
  saveKpiSeasonTarget,
  updateKpiSeason,
} from "./kpiPhaseService";

const { Title } = Typography;
const { Option } = Select;

const SetKPITheoMua = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [kpiList, setKpiList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  // Fetch KPI list on component mount
  useEffect(() => {
    fetchKpiList();
  }, []);

  const fetchKpiList = async () => {
    setLoadingList(true);
    try {
      const branchResponse = await fetchKpiBranches();
      const branchRows = Array.isArray(branchResponse?.data)
        ? branchResponse.data
        : [];
      const seasonResponses = await Promise.all(
        branchRows.map((branch) => fetchKpiSeasons(branch.ChiNhanh))
      );
      setBranches(branchRows);
      setKpiList(
        seasonResponses.flatMap((response, index) =>
          (Array.isArray(response?.data) ? response.data : []).map((season) => ({
            ...season,
            ChiNhanh: Number(
              season.ChiNhanh || branchRows[index]?.ChiNhanh
            ),
            NgayKetThuc:
              season.KPI_NgayKetThuc || season.NgayKetThuc || null,
          }))
        )
      );
    } catch (error) {
      message.error("Lỗi khi tải danh sách KPI");
    } finally {
      setLoadingList(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        Target: parseInt(values.Target, 10) || 0,
        NgayApDung: values.NgayApDung
          ? values.NgayApDung.format("YYYY-MM-DD")
          : null,
        NgayKetThuc: values.NgayKetThuc
          ? values.NgayKetThuc.format("YYYY-MM-DD")
          : null,
        BD: parseInt(values.BD, 10) || 0,
        BNH: parseInt(values.BNH, 10) || 0,
        BNG: parseInt(values.BNG, 10) || 0,
        BanhBan: parseInt(values.BanhBan, 10) || 0,
        BanhDat: parseInt(values.BanhDat, 10) || 0,
      };

      if (editingKpi) {
        await updateKpiSeason(editingKpi.IDMua, {
          TenMuaVu: values.TenMuaVu.trim(),
        });
        await saveKpiSeasonTarget(
          editingKpi.IDMua,
          parseInt(values.ChiNhanh, 10),
          payload
        );
        message.success("Cập nhật KPI thành công");
      } else {
        const createResult = await createKpiSeason({
          TenMuaVu: values.TenMuaVu.trim(),
        });
        const seasonId = createResult?.data?.IDMua;
        if (!seasonId) throw new Error("API không trả IDMua của mùa vụ mới.");
        await saveKpiSeasonTarget(
          seasonId,
          parseInt(values.ChiNhanh, 10),
          payload
        );
        message.success("Thêm KPI thành công");
      }

      form.resetFields();
      setModalVisible(false);
      setEditingKpi(null);
      fetchKpiList(); // Refresh list
    } catch (error) {
      message.error(error.message || "Lỗi khi gửi dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  const handleAddKpi = () => {
    setEditingKpi(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditKpi = (record) => {
    setEditingKpi(record);
    form.setFieldsValue({
      TenMuaVu: record.TenMuaVu,
      Target: record.Target,
      ChiNhanh: record.ChiNhanh,
      NgayApDung: record.NgayApDung ? moment(record.NgayApDung) : null,
      NgayKetThuc: record.NgayKetThuc ? moment(record.NgayKetThuc) : null,
      BD: record.BD,
      BNH: record.BNH,
      BNG: record.BNG,
      BanhBan: record.BanhBan,
      BanhDat: record.BanhDat,
    });
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingKpi(null);
    form.resetFields();
  };

  const columns = [
    {
      title: "Mùa vụ",
      dataIndex: "TenMuaVu",
      key: "TenMuaVu",
    },
    {
      title: "ID mùa",
      dataIndex: "IDMua",
      key: "IDMua",
    },
    {
      title: "Chi Nhánh",
      dataIndex: "ChiNhanh",
      key: "ChiNhanh",
      render: (text) => {
        return (
          branches.find((branch) => Number(branch.ChiNhanh) === Number(text))
            ?.TenChiNhanh || text
        );
      },
    },
    {
      title: "Target",
      dataIndex: "Target",
      key: "Target",
    },
    {
      title: "Ngày Áp Dụng",
      dataIndex: "NgayApDung",
      key: "NgayApDung",
      render: (text) => text ? moment(text).format("DD/MM/YYYY") : "Chưa thiết lập",
    },
    {
      title: "Ngày Kết Thúc",
      dataIndex: "NgayKetThuc",
      key: "NgayKetThuc",
      render: (text) => text ? moment(text).format("DD/MM/YYYY") : "Chưa thiết lập",
    },
    {
      title: "BD",
      dataIndex: "BD",
      key: "BD",
    },
    {
      title: "BNH",
      dataIndex: "BNH",
      key: "BNH",
    },
    {
      title: "BNG",
      dataIndex: "BNG",
      key: "BNG",
    },
    {
      title: "Bánh Bán",
      dataIndex: "BanhBan",
      key: "BanhBan",
    },
    {
      title: "Bánh Đặt",
      dataIndex: "BanhDat",
      key: "BanhDat",
    },
    {
      title: "Thao tác",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEditKpi(record)}>
            Chỉnh sửa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <NavbarTrungThu />
    <div className="banh-ban-container">
      <div className="header-container">
        <Title level={2}>Thiết Lập KPI Bánh Trung Thu</Title>
        <Button type="primary" onClick={handleAddKpi}>
          Thêm KPI
        </Button>
      </div>
      <div
        className="sales-form"
        style={{ width: "100%", display: "block" }}
      >
        <Card
          className="form-card"
          style={{
            width: "100%",
            minWidth: "1200px",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          <Table
            columns={columns.map((col) => ({
              ...col,
              width:
                col.key === "TenMuaVu"
                  ? 160
                  : col.key === "IDMua"
                  ? 90
                  : col.key === "ChiNhanh"
                  ? 150
                  : col.key === "Target"
                  ? 120
                  : col.key === "NgayApDung"
                  ? 120
                  : col.key === "NgayKetThuc"
                  ? 120
                  : col.key === "BD"
                  ? 80
                  : col.key === "BNH"
                  ? 80
                  : col.key === "BNG"
                  ? 80
                  : col.key === "BanhBan"
                  ? 100
                  : col.key === "BanhDat"
                  ? 100
                  : col.key === "action"
                  ? 120
                  : undefined,
            }))}
            dataSource={kpiList}
            loading={loadingList}
            rowKey={(record) => `${record.IDMua}-${record.ChiNhanh}`}
            pagination={{ pageSize: 10 }}
            tableLayout="fixed"
          />
        </Card>
      </div>

      <Modal
        title={editingKpi ? "Chỉnh sửa KPI" : "Thêm KPI"}
        visible={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            BD: 0,
            BNH: 0,
            BNG: 0,
            BanhBan: 0,
            BanhDat: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tên mùa vụ"
                name="TenMuaVu"
                rules={[
                  { required: true, message: "Vui lòng nhập tên mùa vụ!" },
                  { max: 150, message: "Tên mùa vụ tối đa 150 ký tự!" },
                ]}
              >
                <Input placeholder="Ví dụ: Mùa vụ 2026" />
              </Form.Item>
              <Form.Item
                label="Chi Nhánh"
                name="ChiNhanh"
                rules={[
                  { required: true, message: "Vui lòng chọn Chi Nhánh!" },
                ]}
              >
                <Select placeholder="Chọn Chi Nhánh" disabled={Boolean(editingKpi)}>
                  {branches.map((branch) => (
                    <Option key={branch.ChiNhanh} value={branch.ChiNhanh}>
                      {branch.TenChiNhanh}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Target"
                name="Target"
                rules={[{ required: true, message: "Vui lòng nhập Target!" }]}
              >
                <Input placeholder="Nhập Target" />
              </Form.Item>
              <Form.Item
                label="Ngày Áp Dụng"
                name="NgayApDung"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Ngày Kết Thúc"
                name="NgayKetThuc"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Bánh Dẻo" name="BD">
                <Input type="number" placeholder="Nhập BD" />
              </Form.Item>
              <Form.Item label="Bánh Nhí" name="BNH">
                <Input type="number" placeholder="Nhập BNH" />
              </Form.Item>
              <Form.Item label="Bánh Nướng" name="BNG">
                <Input type="number" placeholder="Nhập BNG" />
              </Form.Item>
              <Form.Item label="Bánh Bán" name="BanhBan">
                <Input type="number" placeholder="Nhập Bánh Bán" />
              </Form.Item>
              <Form.Item label="Bánh Đặt" name="BanhDat">
                <Input type="number" placeholder="Nhập Bánh Đặt" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={handleCancel}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingKpi ? "Cập nhật" : "Thêm"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default SetKPITheoMua;
